/**
 * Mock 的 `/admin/**` 路由。
 *
 * 为什么单独一个文件：后台有二十来个端点，每个都要过契约 schema。全塞进 `index.ts`
 * 会把"取数层"（fetch 拦截 + 信封封帧 + 用户侧路由）和"后台业务"混在一起。
 * 这里只做**路由 + 契约校验 + 数据装配**，响应信封（错误码 → HTTP 状态、requestId）
 * 仍由 `index.ts` 统一封：一处实现，不会两边各写一套错误处理。
 *
 * 响应体一律按 packages/contracts 已冻结的 `Admin*` schema 的形状**手工装配**，
 * 而不是复用某个"通用投影"：真实后端的 service 也是手写装配，字段漏一个、多一个、
 * 或者把 null 塞进不可空的字段，都能在契约校验里当场暴露。
 *
 * 校验的是**请求**（query / body 全过 schema）与**响应形状**的可推导性，
 * 不是在复刻真实后端的 SQL 语义：排序、窗口、归并口径对齐 ports.ts 的注释，
 * 但过滤/分页是内存实现（Mock 的数据量本来就只有几十行）。
 */

import {
  AdminListConversationsQuerySchema,
  AdminListMessagesQuerySchema,
  AdminListUsersQuerySchema,
  AdminUsageQuerySchema,
  CreateAdminSchema,
  ErrorCode,
  GrantQuotaSchema,
  MoveLlmModelRouteSchema,
  PatchEmailChannelSchema,
  PatchLlmChannelSchema,
  PatchLlmModelRouteSchema,
  PLAN_LIMITS,
  ResetPasswordSchema,
  SetUserPlanSchema,
  SuspendUserSchema,
  TestEmailChannelSchema,
  UpsertEmailChannelSchema,
  UpsertLlmChannelSchema,
  UpsertLlmModelRouteSchema,
  isUnlimitedQuotaLimit,
  nextMonthlyResetAt,
  startOfUtcMonth,
  type AdminAccount,
  type AdminAccountList,
  type AdminAiStatus,
  type AdminConversationPage,
  type AdminConversationSummary,
  type AdminEmailStatus,
  type AdminListConversationsQuery,
  type AdminMessage,
  type AdminOrderSummary,
  type AdminOverview,
  type AdminPlanDetail,
  type AdminQuotaDetail,
  type AdminUsageGroup,
  type AdminUsageQueryInput,
  type AdminUsageReport,
  type AdminUserDetail,
  type AdminUserSummary,
  type Conversation,
  type EmailChannelList,
  type EmailChannelTestResult,
  type LlmChannelList,
  type LlmChannelTestResult,
  type LlmModelRoute,
  type LlmModelRouteList,
  type Message,
  type Order,
} from '@spirit/contracts';

import {
  activeMockEmailChannel,
  applyGrant,
  committedEntriesBetween,
  createMockAdmin,
  createMockChannel,
  createMockEmailChannel,
  createMockModelRoute,
  deleteMockAdmin,
  deleteMockChannel,
  deleteMockEmailChannel,
  deleteMockModelRoute,
  effectiveStatusOf,
  emailChannelDtoOf,
  existsMockModelRoute,
  findMockChannel,
  findMockEmailChannel,
  findMockModelRoute,
  findMockUser,
  getMockState,
  grantsOf,
  llmChannelDtoOf,
  monthlyGrantedTokensOf,
  modelRouteDtoOf,
  moveMockModelRoute,
  nicknameOf,
  ordersOfUser,
  quotaOfUser,
  quotaStateOf,
  resetMockAdminPassword,
  setPlanForUser,
  sortedMockChannels,
  sortedMockEmailChannels,
  sortedMockModelRoutes,
  subscriptionOfUser,
  suspendMockUser,
  tokenTotalsOf,
  unsuspendMockUser,
  updateMockChannel,
  updateMockEmailChannel,
  updateMockModelRoute,
  usableMockModelRoutes,
  usageOfMessage,
  type MockEmailChannel,
  type MockLedgerEntry,
  type MockLlmChannel,
  type MockUser,
} from './mock-db';

export interface MockAdminRequest {
  method: string;
  path: string;
  search: URLSearchParams;
  body: Record<string, unknown>;
  /** 当前登录账号（`index.ts` 已判过令牌有效、账号未封）。 */
  session: MockUser;
  now: Date;
}

export type MockAdminResult =
  | { readonly ok: true; readonly data: unknown }
  | {
      readonly ok: false;
      readonly code: ErrorCode;
      readonly message?: string;
      readonly details?: Record<string, unknown>;
    };

/* ------------------------------------------------------------------ *
 * 权限
 * ------------------------------------------------------------------ */

/**
 * ROOT 独占的端点（计划文档 §5.1 的权限矩阵）。
 *
 * 管理员能看用户、能封号，但"发放额度 / 改套餐 / 用量报表 / 渠道 / 后台账号"这几类只有 ROOT 能动：
 * 它们要么直接改钱（额度、套餐），要么改系统（渠道密钥、谁能进后台）。
 *
 * 邮箱渠道与 LLM 渠道同属"改系统"：它的密钥能拿平台的域名发信，
 * 拿到就等于能冒充平台给任意用户发邮件——只读都不给管理员。
 */
const ROOT_ONLY_PATTERNS: ReadonlyArray<RegExp> = [
  /^\/admin\/usage$/,
  /^\/admin\/llm-channels(\/.*)?$/,
  /^\/admin\/llm-model-routes(\/.*)?$/,
  /^\/admin\/email-channels(\/.*)?$/,
  /^\/admin\/admins(\/.*)?$/,
  /^\/admin\/users\/[^/]+\/plan$/,
  /^\/admin\/users\/[^/]+\/quota\/grant$/,
];

function isRootOnlyPath(path: string): boolean {
  return ROOT_ONLY_PATTERNS.some((pattern) => pattern.test(path));
}

/* ------------------------------------------------------------------ *
 * 小工具
 * ------------------------------------------------------------------ */

function data(value: unknown): MockAdminResult {
  return { ok: true, data: value };
}

function notOk(code: ErrorCode, message?: string, details?: Record<string, unknown>): MockAdminResult {
  return { ok: false, code, message, details };
}

/** query 转普通对象交给 zod（全部字段都是字符串，数字字段由 schema 的 `coerce` 收）。 */
function queryOf(search: URLSearchParams): Record<string, string> {
  return Object.fromEntries(search.entries());
}

/** zod 的 issue 形状（不 import zod 的类型，只为这点结构引入依赖不划算）。 */
interface SchemaIssue {
  readonly path: ReadonlyArray<string | number>;
  readonly message: string;
}

function issueDetails(where: 'query' | 'body', issues: ReadonlyArray<SchemaIssue>): Record<string, unknown> {
  return {
    where,
    issues: issues.map((issue) => ({ path: issue.path.join('.'), message: issue.message })),
  };
}

function badQuery(issues: ReadonlyArray<SchemaIssue>): MockAdminResult {
  return notOk(ErrorCode.INVALID_PARAM, '请求参数不合法', issueDetails('query', issues));
}

function badBody(issues: ReadonlyArray<SchemaIssue>): MockAdminResult {
  return notOk(ErrorCode.INVALID_PARAM, '请求参数不合法', issueDetails('body', issues));
}

/**
 * 游标分页。
 *
 * 与 `index.ts` 的同名实现是同一套语义（游标 = 上一页最后一条的 key，找不到就从头开始）。
 * 两边不一致的话，同一个列表在用户端与后台会以不同方式翻页，而这种差异只在第二页才看得出来。
 */
function pageOf<T>(
  items: T[],
  limit: number,
  cursor: string | null,
  keyOf: (item: T) => string,
): { items: T[]; nextCursor: string | null } {
  const start = cursor === null ? 0 : Math.max(0, items.findIndex((item) => keyOf(item) === cursor));
  const slice = items.slice(start, start + limit);
  const last = slice[slice.length - 1];
  const nextCursor = last !== undefined && start + limit < items.length ? keyOf(last) : null;
  return { items: slice, nextCursor };
}

/**
 * 后台语境里的"用户"只指普通用户。
 *
 * 这是 `AdminQueryRepository.findUser` 的口径（ports.ts 写明"目标不存在或不是普通用户时返回 null"）：
 * 封号、发额度、改套餐都是针对**消费者账号**的操作，拿一个管理员 id 来调应该当"查不到"，
 * 而不是让管理员把自己封了。
 */
function findAdminUserTarget(userId: string): MockUser | null {
  const user = findMockUser(userId);
  return user !== null && user.role === 'user' ? user : null;
}

/* ------------------------------------------------------------------ *
 * 投影（内部真值 → 契约形状）
 * ------------------------------------------------------------------ */

function adminUserSummaryOf(user: MockUser, now: Date): AdminUserSummary {
  const state = getMockState();
  const conversations = state.conversations.filter((item) => item.userId === user.id);
  const messageCount = state.messages.filter((item) =>
    conversations.some((conversation) => conversation.id === item.conversationId),
  ).length;
  return {
    id: user.id,
    role: user.role,
    nickname: user.nickname,
    phone: user.phone,
    // 邮箱验证码登录的用户没有手机号：不给出邮箱，后台列表里会出现"三个标识列全空"的行，
    // 客服无法核对"这个人是谁"（契约 `AdminUserSummarySchema.email` 的注释就是这个理由）。
    email: user.email,
    status: user.status,
    // `status` 是库里的原值，`effectiveStatus` 是"此刻算不算被封"：
    // 临时封号到期后两者不再相等，后台的"已封禁"筛选要按后者算。
    effectiveStatus: effectiveStatusOf(user, now),
    suspendedAt: user.suspendedAt,
    suspendedUntil: user.suspendedUntil,
    suspendReason: user.suspendReason,
    lastLoginAt: user.lastLoginAt,
    createdAt: user.createdAt,
    // Mock 的 `DELETE /conversations/:id` 是物理删除，没有软删的会话，
    // 所以这里不需要额外的 `deletedAt` 过滤（真实后端要过滤）。
    conversationCount: conversations.length,
    messageCount,
  };
}

function adminUserDetailOf(user: MockUser, now: Date): AdminUserDetail {
  return {
    ...adminUserSummaryOf(user, now),
    avatarUrl: user.avatarUrl,
    profile: user.profile,
  };
}

function adminConversationSummaryOf(conversation: Conversation): AdminConversationSummary {
  return {
    id: conversation.id,
    userId: conversation.userId,
    // 契约要求 1-32 字符且不可空：用户被删掉时给一个明确的占位串，
    // 而不是让契约校验在"账号已删除"这条边上崩掉。
    userNickname: nicknameOf(conversation.userId) ?? '已删除用户',
    title: conversation.title,
    domain: conversation.domain,
    ageBand: conversation.ageBand,
    messageCount: getMockState().messages.filter((item) => item.conversationId === conversation.id).length,
    lastMessageAt: conversation.lastMessageAt,
    createdAt: conversation.createdAt,
  };
}

function adminMessageOf(message: Message): AdminMessage {
  const usage = usageOfMessage(message.id);
  return {
    id: message.id,
    conversationId: message.conversationId,
    role: message.role,
    content: message.content,
    domain: message.domain,
    ageBand: message.ageBand,
    safetyFlag: message.safetyFlag,
    latencyMs: message.latencyMs,
    upstreamModel: message.upstreamModel,
    channelId: usage?.channelId ?? null,
    // 用户消息不产生模型调用，账本里没有它，于是 0/0——而不是把整轮的量摊到两条上。
    promptTokens: usage?.promptTokens ?? 0,
    completionTokens: usage?.completionTokens ?? 0,
    createdAt: message.createdAt,
  };
}

function adminOrderSummaryOf(order: Order): AdminOrderSummary {
  return {
    orderNo: order.orderNo,
    planId: order.planId,
    billingCycle: order.billingCycle,
    kind: order.kind,
    amountFen: order.amountFen,
    status: order.status,
    channel: order.channel,
    paidAt: order.paidAt,
    createdAt: order.createdAt,
  };
}

function adminQuotaDetailOf(userId: string, now: Date): AdminQuotaDetail {
  const quota = quotaStateOf(userId);
  const limits = PLAN_LIMITS[quota.planId];
  const snapshot = quotaOfUser(userId, now);
  const monthFrom = new Date(startOfUtcMonth(now)).getTime();
  const monthTo = new Date(nextMonthlyResetAt(now)).getTime();
  return {
    planId: quota.planId,
    // `unlimited` 表示"这个档位不设闸"：日/月**两维**都不设闸才算，与额度快照同口径。
    unlimited: isUnlimitedQuotaLimit(limits.dailyTokenLimit) && isUnlimitedQuotaLimit(limits.monthlyTokenLimit),
    dailyUsedTokens: quota.dailyTokenUsed,
    dailyLimitTokens: isUnlimitedQuotaLimit(limits.dailyTokenLimit) ? null : limits.dailyTokenLimit,
    dailyResetAt: quota.dailyResetAt,
    monthlyUsedTokens: quota.monthlyTokenUsed,
    monthlyGrantedTokens: monthlyGrantedTokensOf(userId, now),
    monthlyLimitTokens: isUnlimitedQuotaLimit(limits.monthlyTokenLimit) ? null : limits.monthlyTokenLimit,
    monthlyResetAt: quota.monthlyResetAt,
    // 剩余百分比直接取额度快照（"最先撞上的那一维"）：后台与用户端必须是同一个数，
    // 各算一遍迟早会分叉。
    remainingPercent: snapshot?.remainingPercent ?? 0,
    // 契约的配额详情是**本月**口径（上面那个 `monthlyGrantedTokens` 就是本月合计），
    // 发放列表必须落在同一窗口，否则"合计"和明细对不上。
    grants: grantsOf(userId)
      .filter((grant) => {
        const at = new Date(grant.createdAt).getTime();
        return at >= monthFrom && at < monthTo;
      })
      .map((grant) => ({
        id: grant.id,
        totalTokens: grant.totalTokens,
        reason: grant.reason,
        grantedByUserId: grant.grantedByUserId,
        createdAt: grant.createdAt,
      })),
  };
}

function adminPlanDetailOf(userId: string): AdminPlanDetail {
  const subscription = subscriptionOfUser(userId);
  return {
    planId: quotaStateOf(userId).planId,
    billingCycle: subscription?.billingCycle ?? null,
    startsAt: subscription?.startsAt ?? null,
    expiresAt: subscription?.expiresAt ?? null,
    orderNo: subscription?.orderNo ?? null,
    orders: ordersOfUser(userId).map(adminOrderSummaryOf),
  };
}

function adminAccountOf(user: MockUser): AdminAccount {
  return {
    id: user.id,
    // 只有后台账号会被投影成 `AdminAccount`，它们的 `username` 必然存在。
    username: user.username ?? '',
    nickname: user.nickname,
    role: user.role,
    status: user.status,
    lastLoginAt: user.lastLoginAt,
    passwordUpdatedAt: user.passwordUpdatedAt,
    createdAt: user.createdAt,
  };
}

/**
 * 模型路由列表 + 当前生效模型（`AdminModelRoutesService.list()` 的同构实现）。
 *
 * 生效模型不由本函数"取 priority 最小的那行"：候选还要看渠道是否停用、密钥是否可用，
 * 另算一遍必然与真实路由不一致，而这一页就是用来回答"现在到底用哪个模型"的。
 * 所以它与 `adminAiStatusOf()` / `llmChannelListOf()` 共用同一次过滤（`usableMockModelRoutes()`）。
 *
 * `source` 的 `'none'` 档在 Mock 里不会出现：一条可用候选都没有时，回复由本地话术生成，
 * 报 `mock` 比报"没有可用候选"更贴事实（与 `adminEmailStatusOf()` 的取舍同构）。
 */
function llmModelRouteListOf(): LlmModelRouteList {
  const usable = usableMockModelRoutes();
  // 顺序即优先级：`usableMockModelRoutes()` 已经按全局候选顺序排好，过滤不会打乱它。
  const chat = usable.filter((route) => route.role === 'chat' || route.role === 'both');
  const router = usable.filter((route) => route.role === 'router' || route.role === 'both');
  return {
    items: sortedMockModelRoutes(),
    activeChatModel: chat[0]?.model ?? null,
    activeRouterModel: router[0]?.model ?? null,
    activeChannelId: chat[0]?.channelId ?? null,
    source: chat.length > 0 || router.length > 0 ? 'channel' : 'mock',
  };
}

function adminAiStatusOf(): AdminAiStatus {
  const list = llmModelRouteListOf();
  const channel = list.activeChannelId === null ? null : findMockChannel(list.activeChannelId);
  return {
    // 没有可用的对话候选时，Mock 的回复由本地话术生成——与真实后端"一条候选都没有就走
    // AI_MOCK"的兜底同形：`available` 为 false 但对话仍然可用。
    available: list.activeChatModel !== null,
    mock: list.activeChatModel === null,
    // 报的是本地话术用的模型名（真实后端那里是 `MOCK_MODEL_NAME`），不是"某个渠道的模型"。
    model: list.activeChatModel ?? 'deepseek-chat',
    reason: list.activeChatModel === null ? 'Mock 没有可用的模型候选，回复由本地话术生成' : null,
    channelName: channel?.name ?? null,
    source: list.source,
  };
}

function adminOverviewOf(now: Date): AdminOverview {
  const state = getMockState();
  const consumers = state.users.filter((user) => user.role === 'user');
  const totals = tokenTotalsOf(now);
  return {
    userCount: consumers.length,
    activeUserCount: consumers.filter((user) => effectiveStatusOf(user, now) === 'active').length,
    suspendedUserCount: consumers.filter((user) => effectiveStatusOf(user, now) === 'suspended').length,
    // `adminCount` 把 ROOT 算进去：后台账号页把 ROOT 单列，但"有几个账号能进后台"这件事
    // 少算一个，屏幕上就会写着"管理员 1 个"却有两个账号能登录。
    adminCount: state.users.filter((user) => user.role !== 'user').length,
    conversationCount: state.conversations.length,
    messageCount: state.messages.length,
    todayTokens: totals.todayTokens,
    monthTokens: totals.monthTokens,
    channelCount: state.llmChannels.length,
    enabledChannelCount: state.llmChannels.filter((channel) => channel.enabled).length,
    ai: adminAiStatusOf(),
    email: adminEmailStatusOf(),
  };
}

/** 用量报表的分组键与展示名。`groupBy` 的四个取值都在契约里冻结了。 */
function usageKeyOf(entry: MockLedgerEntry, groupBy: AdminUsageQueryInput['groupBy']): { key: string; label: string } {
  if (groupBy === 'user') {
    return { key: entry.userId, label: nicknameOf(entry.userId) ?? entry.userId };
  }
  if (groupBy === 'channel') {
    /*
     * 契约的 `AdminUsageGroupSchema.key` **不可为 null**，而仓储层的分组键允许为 null
     * （未归因单独成组、不丢行）。真实后端 `apps/server/src/admin/admin-usage.service.ts`
     * 的 `UNATTRIBUTED_KEY` 用的是**空串**，Mock 必须逐字对齐同一个哨兵——两边不一致时，
     * 同一份数据在"本地 Mock"和"真后端"两种模式下会分成两个不同的分组键。
     */
    return entry.channelId === null
      ? { key: '', label: '未归因' }
      : { key: entry.channelId, label: findMockChannel(entry.channelId)?.name ?? entry.channelId };
  }
  if (groupBy === 'model') {
    return entry.model === null
      ? { key: '', label: '未归因' }
      : { key: entry.model, label: entry.model };
  }
  // day：UTC 自然日 `YYYY-MM-DD`。用本地时区会让"今天"和额度页差一天。
  const day = entry.createdAt.slice(0, 10);
  return { key: day, label: day };
}

/** 报表窗口：没给就用当前 UTC 自然月（与额度月度窗口同一个区间）。 */
function usageWindowOf(query: AdminUsageQueryInput, now: Date): { from: Date; to: Date } {
  return {
    from: query.from === undefined ? new Date(startOfUtcMonth(now)) : new Date(query.from),
    to: query.to === undefined ? new Date(nextMonthlyResetAt(now)) : new Date(query.to),
  };
}

function adminUsageReportOf(query: AdminUsageQueryInput, from: Date, to: Date): AdminUsageReport {
  // 只统计 `kind === 'commit'`、且**不**按软删过滤：token 已经烧掉了，
  // 会话删得掉、账删不掉（与真实后端 sumCommittedBetween / aggregateUsage 的例外口径一致）。
  const groups = new Map<string, AdminUsageGroup>();
  for (const entry of committedEntriesBetween(from, to)) {
    const { key, label } = usageKeyOf(entry, query.groupBy);
    const current = groups.get(key);
    if (current === undefined) {
      groups.set(key, {
        key,
        label,
        promptTokens: entry.promptTokens,
        completionTokens: entry.completionTokens,
        totalTokens: entry.totalTokens,
        callCount: 1,
      });
      continue;
    }
    current.promptTokens += entry.promptTokens;
    current.completionTokens += entry.completionTokens;
    current.totalTokens += entry.totalTokens;
    current.callCount += 1;
  }

  /*
   * 排序与截断在契约里是 **service** 的活（ports.ts 的注释要求两套实现口径一致）：
   * 合计降序 → key 升序（同额时顺序稳定，不然翻页/对比会看到两组来回换位）。
   * `limit` 只截断展示，`total` 仍是**全窗口**真值——截断过的合计会让"总量"看起来变小。
   */
  const sorted = [...groups.values()].sort(
    (a, b) => b.totalTokens - a.totalTokens || a.key.localeCompare(b.key),
  );
  const total = sorted.reduce(
    (sum, group) => ({
      promptTokens: sum.promptTokens + group.promptTokens,
      completionTokens: sum.completionTokens + group.completionTokens,
      totalTokens: sum.totalTokens + group.totalTokens,
      callCount: sum.callCount + group.callCount,
    }),
    { promptTokens: 0, completionTokens: 0, totalTokens: 0, callCount: 0 },
  );

  return {
    groupBy: query.groupBy,
    from: from.toISOString(),
    to: to.toISOString(),
    groups: sorted.slice(0, query.limit),
    total,
  };
}

function conversationPageOf(
  conversations: Conversation[],
  query: AdminListConversationsQuery,
): AdminConversationPage {
  const keyword = query.keyword?.trim().toLowerCase();
  const matched = conversations.filter((conversation) => {
    if (query.userId !== undefined && conversation.userId !== query.userId) {
      return false;
    }
    if (keyword !== undefined && keyword !== '' && !conversation.title.toLowerCase().includes(keyword)) {
      return false;
    }
    return true;
  });
  // 与真实后端 `listConversations` 同一套排序：lastMessageAt 降序、id 降序。
  matched.sort((a, b) => b.lastMessageAt.localeCompare(a.lastMessageAt) || b.id.localeCompare(a.id));
  const page = pageOf(matched, query.limit, query.cursor ?? null, (item) => item.id);
  return { items: page.items.map(adminConversationSummaryOf), nextCursor: page.nextCursor };
}

function llmChannelListOf(): LlmChannelList {
  const active = llmModelRouteListOf();
  return {
    items: sortedMockChannels().map(llmChannelDtoOf),
    // 与 `AdminChannelsService.list()` 同口径：生效渠道取**候选顺序里第一条对话模型**所属渠道，
    // 而不是"enabled 里优先级最高的那个"——渠道是否真在承载流量由模型路由表决定。
    activeChannelId: active.activeChannelId,
    // `none` 是"一条可用候选都没有"；Mock 此时由本地话术回复，所以报 `mock`（理由见
    // `llmModelRouteListOf()`）。渠道表是唯一来源，因此不会有 `env` 这一档。
    source: active.source,
  };
}

function emailChannelListOf(): EmailChannelList {
  const active = activeMockEmailChannel();
  return {
    items: sortedMockEmailChannels().map(emailChannelDtoOf),
    activeChannelId: active?.id ?? null,
    // 与 `llmChannelListOf()` 同一口径：Mock 没有"环境变量兜底"这一层。
    source: active === null ? 'mock' : 'channel',
  };
}

/**
 * 邮箱发信能力投影（`AdminOverviewSchema.email` 是必填，后台概览靠它回答
 * "邮箱验证码登录现在能不能用"）。
 *
 * 没有启用渠道时**不是** `none`：Mock 的验证码本来就发得出去（登录页会直接把固定码写在提示里），
 * 只是没走真实通道。这与 `adminAiStatusOf()` 里"没渠道也照样能聊"的兜底同构，
 * 报 `mock` + reason 比报"未配置"更贴事实——后者会让人以为邮箱登录整个不可用。
 */
function adminEmailStatusOf(): AdminEmailStatus {
  const channel = activeMockEmailChannel();
  return {
    configured: channel !== null,
    mock: channel === null,
    fromAddress: channel?.fromAddress ?? null,
    source: channel === null ? 'mock' : 'channel',
    channelName: channel?.name ?? null,
    reason: channel === null ? 'Mock 未启用邮箱渠道，验证码由登录页直接提示' : null,
  };
}

function channelTestResultOf(channel: MockLlmChannel): LlmChannelTestResult {
  return {
    // Mock 不会真的打上游。契约要求"上游失败也包在 code:0 里"，
    // 这里永远 ok:true，但 message 要说清楚它只验证了配置形状，
    // 免得有人以为"测试通过"等于密钥和网络都没问题。
    ok: true,
    httpStatus: 200,
    latencyMs: 42,
    message: `Mock 未发起真实请求；渠道「${channel.name}」配置形状合法`,
    // v1.6.0 起渠道行上没有模型列表了：走 `GET /models` 拿到几个就算几个，
    // Mock 用"挂在这条渠道上的模型路由条数"回答同一个问题。
    modelCount: sortedMockModelRoutes().filter((route) => route.channelId === channel.id).length,
  };
}

function emailChannelTestResultOf(channel: MockEmailChannel): EmailChannelTestResult {
  return {
    // 与 `channelTestResultOf()` 同一约定：Mock 不发真信，只回一个形状合法的结果，
    // 并明确写出"没发"——测试发信这个功能最怕的就是"提示成功但其实什么都没发生"。
    ok: true,
    httpStatus: 200,
    latencyMs: 42,
    message: `Mock 未真实发信；渠道「${channel.name}」配置形状合法`,
  };
}

function adminAccountListOf(): AdminAccountList {
  const staff = getMockState().users.filter((user) => user.role !== 'user');
  const root = staff.find((user) => user.role === 'root');
  const admins = staff
    .filter((user) => user.role === 'admin')
    // createdAt 升序：先加的排前面（`rootAccount` 单列，不参与这里的排序）。
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id));
  return {
    items: admins.map(adminAccountOf),
    rootAccount: root === undefined ? null : adminAccountOf(root),
  };
}

/* ------------------------------------------------------------------ *
 * 路由
 * ------------------------------------------------------------------ */

/** 非 `/admin/**` 返回 null（交给调用方继续路由）。 */
export function handleAdminRoute(request: MockAdminRequest): MockAdminResult | null {
  const { method, path, search, body, session, now } = request;

  /*
   * 后台是员工入口：普通用户连"这个路径存不存在"都不该知道，所以先按角色挡住。
   *
   * 这一条必须排在路径匹配之前——只要放行一次，`GET /admin/overview` 就会把全站
   * 用户数、消息数、渠道数交给任意登录用户，而那是权限矩阵里明确只给 admin/root 的数据。
   */
  if (session.role === 'user') {
    return notOk(ErrorCode.FORBIDDEN, '当前身份无权访问该功能');
  }

  /*
   * 先判权限、再判路径是否存在。
   *
   * 顺序反过来的话，管理员探测一个自己无权访问的路径会拿到 40400（"这里什么都没有"），
   * 而那应该是 40300（"这不是你能碰的"）——两者的错误码语义完全不同。
   */
  if (isRootOnlyPath(path) && session.role !== 'root') {
    return notOk(ErrorCode.FORBIDDEN, '当前身份无权访问该功能');
  }

  if (method === 'GET' && path === '/admin/overview') {
    return data(adminOverviewOf(now));
  }

  // 页面右上角的 AI 状态：不在权限矩阵里，但它是后台外壳每次都要拉的只读信息。
  if (method === 'GET' && path === '/admin/ai-status') {
    return data(adminAiStatusOf());
  }

  if (method === 'GET' && path === '/admin/users') {
    const parsed = AdminListUsersQuerySchema.safeParse(queryOf(search));
    if (!parsed.success) {
      return badQuery(parsed.error.issues);
    }
    const { keyword, status, role, cursor, limit } = parsed.data;
    /*
     * 不传 `role` 时只看普通用户：详情 / 封号 / 配额端点都只认 `role === 'user'`
     * （`findAdminUserTarget` 的口径），列表若默认把管理员也列出来，就会出现
     * "点进去 40400"的行。要看后台账号用 `?role=admin` / `?role=root`。
     */
    const matched = getMockState().users.filter((user) => {
      if (role === undefined ? user.role !== 'user' : user.role !== role) {
        return false;
      }
      if (status !== undefined && effectiveStatusOf(user, now) !== status) {
        return false;
      }
      if (keyword !== undefined && keyword.trim() !== '') {
        const needle = keyword.trim().toLowerCase();
        const haystack = `${user.nickname} ${user.phone ?? ''}`.toLowerCase();
        if (!haystack.includes(needle)) {
          return false;
        }
      }
      return true;
    });
    // 与真实后端 `listUsers` 一致：createdAt 降序、id 降序（游标是 id，顺序不稳会在翻页边界丢行）。
    matched.sort((a, b) => b.createdAt.localeCompare(a.createdAt) || b.id.localeCompare(a.id));
    const page = pageOf(matched, limit, cursor ?? null, (user) => user.id);
    const items: AdminUserSummary[] = page.items.map((user) => adminUserSummaryOf(user, now));
    return data({ items, nextCursor: page.nextCursor });
  }

  const userConversations = /^\/admin\/users\/([^/]+)\/conversations$/.exec(path);
  if (userConversations !== null && method === 'GET') {
    const user = findAdminUserTarget(decodeURIComponent(userConversations[1] ?? ''));
    if (user === null) {
      return notOk(ErrorCode.NOT_FOUND, '用户不存在');
    }
    const parsed = AdminListConversationsQuerySchema.safeParse(queryOf(search));
    if (!parsed.success) {
      return badQuery(parsed.error.issues);
    }
    const conversations = getMockState().conversations.filter((item) => item.userId === user.id);
    return data(conversationPageOf(conversations, parsed.data));
  }

  const userQuotaGrant = /^\/admin\/users\/([^/]+)\/quota\/grant$/.exec(path);
  if (userQuotaGrant !== null && method === 'POST') {
    const user = findAdminUserTarget(decodeURIComponent(userQuotaGrant[1] ?? ''));
    if (user === null) {
      return notOk(ErrorCode.NOT_FOUND, '用户不存在');
    }
    const parsed = GrantQuotaSchema.safeParse(body);
    if (!parsed.success) {
      return badBody(parsed.error.issues);
    }
    // 发放人记的是**当前登录账号**（审计要用），不是写死 ROOT。
    applyGrant(user.id, parsed.data, session.id, now);
    return data(adminQuotaDetailOf(user.id, now));
  }

  const userQuota = /^\/admin\/users\/([^/]+)\/quota$/.exec(path);
  if (userQuota !== null && method === 'GET') {
    const user = findAdminUserTarget(decodeURIComponent(userQuota[1] ?? ''));
    if (user === null) {
      return notOk(ErrorCode.NOT_FOUND, '用户不存在');
    }
    return data(adminQuotaDetailOf(user.id, now));
  }

  const userPlan = /^\/admin\/users\/([^/]+)\/plan$/.exec(path);
  if (userPlan !== null && method === 'PUT') {
    const user = findAdminUserTarget(decodeURIComponent(userPlan[1] ?? ''));
    if (user === null) {
      return notOk(ErrorCode.NOT_FOUND, '用户不存在');
    }
    const parsed = SetUserPlanSchema.safeParse(body);
    if (!parsed.success) {
      return badBody(parsed.error.issues);
    }
    setPlanForUser(user.id, parsed.data, now);
    return data(adminPlanDetailOf(user.id));
  }

  const userPlans = /^\/admin\/users\/([^/]+)\/plans$/.exec(path);
  if (userPlans !== null && method === 'GET') {
    const user = findAdminUserTarget(decodeURIComponent(userPlans[1] ?? ''));
    if (user === null) {
      return notOk(ErrorCode.NOT_FOUND, '用户不存在');
    }
    return data(adminPlanDetailOf(user.id));
  }

  const userSuspend = /^\/admin\/users\/([^/]+)\/suspend$/.exec(path);
  if (userSuspend !== null && method === 'POST') {
    const user = findAdminUserTarget(decodeURIComponent(userSuspend[1] ?? ''));
    if (user === null) {
      return notOk(ErrorCode.NOT_FOUND, '用户不存在');
    }
    const parsed = SuspendUserSchema.safeParse(body);
    if (!parsed.success) {
      return badBody(parsed.error.issues);
    }
    const updated = suspendMockUser(user.id, parsed.data, now);
    if (updated === null) {
      return notOk(ErrorCode.NOT_FOUND, '用户不存在');
    }
    // 返回**详情**（不是 204）：前端封完要立刻用新状态重绘那一行，
    // 让客户端再拉一次详情会多一次往返，也给了"刚写的状态还没生效"的错觉。
    return data(adminUserDetailOf(updated, now));
  }

  const userUnsuspend = /^\/admin\/users\/([^/]+)\/unsuspend$/.exec(path);
  if (userUnsuspend !== null && method === 'POST') {
    const user = findAdminUserTarget(decodeURIComponent(userUnsuspend[1] ?? ''));
    if (user === null) {
      return notOk(ErrorCode.NOT_FOUND, '用户不存在');
    }
    const updated = unsuspendMockUser(user.id, now);
    if (updated === null) {
      return notOk(ErrorCode.NOT_FOUND, '用户不存在');
    }
    return data(adminUserDetailOf(updated, now));
  }

  const userById = /^\/admin\/users\/([^/]+)$/.exec(path);
  if (userById !== null && method === 'GET') {
    const user = findAdminUserTarget(decodeURIComponent(userById[1] ?? ''));
    if (user === null) {
      return notOk(ErrorCode.NOT_FOUND, '用户不存在');
    }
    return data(adminUserDetailOf(user, now));
  }

  if (method === 'GET' && path === '/admin/conversations') {
    const parsed = AdminListConversationsQuerySchema.safeParse(queryOf(search));
    if (!parsed.success) {
      return badQuery(parsed.error.issues);
    }
    return data(conversationPageOf(getMockState().conversations, parsed.data));
  }

  const conversationMessages = /^\/admin\/conversations\/([^/]+)\/messages$/.exec(path);
  if (conversationMessages !== null && method === 'GET') {
    const id = decodeURIComponent(conversationMessages[1] ?? '');
    const conversation = getMockState().conversations.find((item) => item.id === id);
    if (conversation === undefined) {
      return notOk(ErrorCode.NOT_FOUND, '会话不存在');
    }
    const parsed = AdminListMessagesQuerySchema.safeParse(queryOf(search));
    if (!parsed.success) {
      return badQuery(parsed.error.issues);
    }
    const ordered = getMockState()
      .messages.filter((item) => item.conversationId === id)
      // 与真实后端 `listMessages` 一致：createdAt 升序、id 升序（越翻越新）。
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id));
    const page = pageOf(ordered, parsed.data.limit, parsed.data.cursor ?? null, (item) => item.id);
    return data({ items: page.items.map(adminMessageOf), nextCursor: page.nextCursor });
  }

  if (method === 'GET' && path === '/admin/usage') {
    const parsed = AdminUsageQuerySchema.safeParse(queryOf(search));
    if (!parsed.success) {
      return badQuery(parsed.error.issues);
    }
    const { from, to } = usageWindowOf(parsed.data, now);
    // 反向窗口要报 40001，而不是返回一份空报表：空报表看起来像"这段时间真没消耗"。
    if (!(from.getTime() < to.getTime())) {
      return notOk(ErrorCode.INVALID_PARAM, '请求参数不合法', {
        where: 'query',
        issues: [{ path: 'from', message: 'from 必须早于 to' }],
      });
    }
    return data(adminUsageReportOf(parsed.data, from, to));
  }

  if (path === '/admin/llm-channels' && method === 'GET') {
    return data(llmChannelListOf());
  }

  if (path === '/admin/llm-channels' && method === 'POST') {
    const parsed = UpsertLlmChannelSchema.safeParse(body);
    if (!parsed.success) {
      return badBody(parsed.error.issues);
    }
    return data(llmChannelDtoOf(createMockChannel(parsed.data)));
  }

  const channelTest = /^\/admin\/llm-channels\/([^/]+)\/test$/.exec(path);
  if (channelTest !== null && method === 'POST') {
    const channel = findMockChannel(decodeURIComponent(channelTest[1] ?? ''));
    if (channel === null) {
      return notOk(ErrorCode.NOT_FOUND, '渠道不存在');
    }
    return data(channelTestResultOf(channel));
  }

  const channelById = /^\/admin\/llm-channels\/([^/]+)$/.exec(path);
  if (channelById !== null && method === 'PATCH') {
    const id = decodeURIComponent(channelById[1] ?? '');
    const parsed = PatchLlmChannelSchema.safeParse(body);
    if (!parsed.success) {
      return badBody(parsed.error.issues);
    }
    // 契约的 PATCH body 必带 `id`：和路径不一致说明调用方拼错了请求，
    // 静默按路径改会掩盖一个真实的前端 bug。
    if (parsed.data.id !== id) {
      return notOk(ErrorCode.INVALID_PARAM, '请求体里的 id 与路径不一致');
    }
    const updated = updateMockChannel(id, parsed.data);
    if (updated === null) {
      return notOk(ErrorCode.NOT_FOUND, '渠道不存在');
    }
    return data(llmChannelDtoOf(updated));
  }

  if (channelById !== null && method === 'DELETE') {
    const removed = deleteMockChannel(decodeURIComponent(channelById[1] ?? ''));
    if (!removed) {
      return notOk(ErrorCode.NOT_FOUND, '渠道不存在');
    }
    // DELETE 一律 `data: null`（与用户侧端点同一约定）。
    return data(null);
  }

  /*
   * 模型路由（「模型优先级」页）：全局 CRUD + 上移/下移。
   *
   * 两条业务规则在这里判而不是在 `mock-db` 里：渠道必须存在（40400）与
   * 同一渠道下模型名唯一（40900）是两个**不同的错误码**，仓储层分不清该回哪个
   * ——与 `POST /admin/llm-channels` 的分工一致。
   */
  if (path === '/admin/llm-model-routes' && method === 'GET') {
    return data(llmModelRouteListOf());
  }

  if (path === '/admin/llm-model-routes' && method === 'POST') {
    const parsed = UpsertLlmModelRouteSchema.safeParse(body);
    if (!parsed.success) {
      return badBody(parsed.error.issues);
    }
    if (findMockChannel(parsed.data.channelId) === null) {
      return notOk(ErrorCode.NOT_FOUND, '渠道不存在');
    }
    if (existsMockModelRoute(parsed.data.channelId, parsed.data.model)) {
      return notOk(ErrorCode.CONFLICT, '该渠道下已存在同名模型', {
        where: 'body',
        issues: [{ path: 'model', message: '同一渠道下的模型名必须唯一' }],
      });
    }
    const created = createMockModelRoute(parsed.data);
    const channel = findMockChannel(created.channelId);
    return channel === null
      ? notOk(ErrorCode.NOT_FOUND, '渠道不存在')
      : data(modelRouteDtoOf(created, channel));
  }

  const modelRouteMove = /^\/admin\/llm-model-routes\/([^/]+)\/move$/.exec(path);
  if (modelRouteMove !== null && method === 'POST') {
    const parsed = MoveLlmModelRouteSchema.safeParse(body);
    if (!parsed.success) {
      return badBody(parsed.error.issues);
    }
    const id = decodeURIComponent(modelRouteMove[1] ?? '');
    if (findMockModelRoute(id) === null) {
      return notOk(ErrorCode.NOT_FOUND, '模型路由不存在');
    }
    /*
     * 边界（首行上移 / 末行下移）**不报错**：`moveMockModelRoute()` 返回 false 表示
     * "没什么可做"，此时回的就是原列表。按钮置灰是 UI 细节，接口不该因此失败。
     */
    moveMockModelRoute(id, parsed.data.direction);
    return data(llmModelRouteListOf());
  }

  const modelRouteById = /^\/admin\/llm-model-routes\/([^/]+)$/.exec(path);
  if (modelRouteById !== null && method === 'PATCH') {
    const id = decodeURIComponent(modelRouteById[1] ?? '');
    const parsed = PatchLlmModelRouteSchema.safeParse(body);
    if (!parsed.success) {
      return badBody(parsed.error.issues);
    }
    // 同渠道 PATCH：契约 body 必带 `id`，与路径不一致说明调用方拼错了请求。
    if (parsed.data.id !== id) {
      return notOk(ErrorCode.INVALID_PARAM, '请求体里的 id 与路径不一致');
    }
    const existing = findMockModelRoute(id);
    if (existing === null) {
      return notOk(ErrorCode.NOT_FOUND, '模型路由不存在');
    }
    const nextChannelId = parsed.data.channelId ?? existing.channelId;
    const nextModel = parsed.data.model ?? existing.model;
    if (findMockChannel(nextChannelId) === null) {
      return notOk(ErrorCode.NOT_FOUND, '渠道不存在');
    }
    // 只有真的动了 (channelId, model) 才查重：改个备注不该因为"自己和自己重名"而失败。
    if (
      (nextChannelId !== existing.channelId || nextModel !== existing.model) &&
      existsMockModelRoute(nextChannelId, nextModel, id)
    ) {
      return notOk(ErrorCode.CONFLICT, '该渠道下已存在同名模型', {
        where: 'body',
        issues: [{ path: 'model', message: '同一渠道下的模型名必须唯一' }],
      });
    }
    const updated = updateMockModelRoute(id, parsed.data);
    if (updated === null) {
      return notOk(ErrorCode.NOT_FOUND, '模型路由不存在');
    }
    const channel = findMockChannel(updated.channelId);
    return channel === null
      ? notOk(ErrorCode.NOT_FOUND, '渠道不存在')
      : data(modelRouteDtoOf(updated, channel));
  }

  if (modelRouteById !== null && method === 'DELETE') {
    const removed = deleteMockModelRoute(decodeURIComponent(modelRouteById[1] ?? ''));
    if (!removed) {
      return notOk(ErrorCode.NOT_FOUND, '模型路由不存在');
    }
    return data(null);
  }

  /*
   * 邮箱渠道：与上面的 LLM 渠道逐句同构（列表 / 新建 / 测试发信 / 局部更新 / 删除）。
   * 分成两套而不是抽一个"通用渠道"是因为契约里它们是两张表、两组 schema：
   * 邮箱渠道有 `fromAddress` / `fromName`、LLM 渠道有 `provider` / `timeoutMs`，
   * 硬合并只能靠可选字段兜，那样"漏传一个必填字段"就不再是编译期错误。
   */
  if (path === '/admin/email-channels' && method === 'GET') {
    return data(emailChannelListOf());
  }

  if (path === '/admin/email-channels' && method === 'POST') {
    const parsed = UpsertEmailChannelSchema.safeParse(body);
    if (!parsed.success) {
      return badBody(parsed.error.issues);
    }
    return data(emailChannelDtoOf(createMockEmailChannel(parsed.data)));
  }

  const emailChannelTest = /^\/admin\/email-channels\/([^/]+)\/test$/.exec(path);
  if (emailChannelTest !== null && method === 'POST') {
    const channel = findMockEmailChannel(decodeURIComponent(emailChannelTest[1] ?? ''));
    if (channel === null) {
      return notOk(ErrorCode.NOT_FOUND, '邮箱渠道不存在');
    }
    // `to` 是必填（契约 `TestEmailChannelSchema`）：收件地址非法时前端会先被挡一次，
    // 这里再校验一次，手写请求绕过前端时也不会让 Mock 收下一个空收件人。
    const parsed = TestEmailChannelSchema.safeParse(body);
    if (!parsed.success) {
      return badBody(parsed.error.issues);
    }
    return data(emailChannelTestResultOf(channel));
  }

  const emailChannelById = /^\/admin\/email-channels\/([^/]+)$/.exec(path);
  if (emailChannelById !== null && method === 'PATCH') {
    const id = decodeURIComponent(emailChannelById[1] ?? '');
    const parsed = PatchEmailChannelSchema.safeParse(body);
    if (!parsed.success) {
      return badBody(parsed.error.issues);
    }
    if (parsed.data.id !== id) {
      return notOk(ErrorCode.INVALID_PARAM, '请求体里的 id 与路径不一致');
    }
    const updated = updateMockEmailChannel(id, parsed.data);
    if (updated === null) {
      return notOk(ErrorCode.NOT_FOUND, '邮箱渠道不存在');
    }
    return data(emailChannelDtoOf(updated));
  }

  if (emailChannelById !== null && method === 'DELETE') {
    const removed = deleteMockEmailChannel(decodeURIComponent(emailChannelById[1] ?? ''));
    if (!removed) {
      return notOk(ErrorCode.NOT_FOUND, '邮箱渠道不存在');
    }
    return data(null);
  }

  if (path === '/admin/admins' && method === 'GET') {
    return data(adminAccountListOf());
  }

  if (path === '/admin/admins' && method === 'POST') {
    const parsed = CreateAdminSchema.safeParse(body);
    if (!parsed.success) {
      return badBody(parsed.error.issues);
    }
    const created = createMockAdmin(parsed.data, now);
    if (created === 'conflict') {
      return notOk(ErrorCode.CONFLICT, '账号名已存在');
    }
    return data(adminAccountOf(created));
  }

  /*
   * 改口令接受两种路径形状：
   * `PATCH /admin/admins/:id/password` 是 web 侧 `admin-api.ts` 真正调用的那个，
   * 而计划文档的权限矩阵把它写成"PATCH /admin/admins*"（即改账号本体）。
   * 两者映射到同一个动作（改口令、返回 `data:null`），都收下——否则总有一种读法
   * 在 Mock 模式下走不通，而那时报错信息看起来像后端没实现。
   */
  const adminPasswordTarget =
    /^\/admin\/admins\/([^/]+)\/password$/.exec(path) ?? /^\/admin\/admins\/([^/]+)$/.exec(path);
  if (adminPasswordTarget !== null && method === 'PATCH') {
    const parsed = ResetPasswordSchema.safeParse(body);
    if (!parsed.success) {
      return badBody(parsed.error.issues);
    }
    const accountId = decodeURIComponent(adminPasswordTarget[1] ?? '');
    const account = findMockUser(accountId);
    if (account === null || account.role === 'user') {
      return notOk(ErrorCode.NOT_FOUND, '后台账号不存在');
    }
    // 允许重置**自己**的口令（改完继续用新口令登录）；只有"删自己"才要挡。
    const updated = resetMockAdminPassword(account.id, parsed.data.password, now);
    if (!updated) {
      return notOk(ErrorCode.NOT_FOUND, '后台账号不存在');
    }
    return data(null);
  }

  const adminById = /^\/admin\/admins\/([^/]+)$/.exec(path);
  if (adminById !== null && method === 'DELETE') {
    const accountId = decodeURIComponent(adminById[1] ?? '');
    const account = findMockUser(accountId);
    if (account === null || account.role === 'user') {
      return notOk(ErrorCode.NOT_FOUND, '后台账号不存在');
    }
    // ROOT 是恢复出厂设置的唯一入口：它被删掉就没人能再发额度、改渠道了。
    if (account.role === 'root') {
      return notOk(ErrorCode.CONFLICT, 'ROOT 账号不可删除');
    }
    // 删掉自己会让当前会话立刻指向一个不存在的账号（下一次请求就被登出），
    // 这几乎总是误操作，明确挡住比"删完神秘掉线"好。
    if (account.id === session.id) {
      return notOk(ErrorCode.CONFLICT, '不能删除当前登录的账号');
    }
    const removed = deleteMockAdmin(account.id);
    if (!removed) {
      return notOk(ErrorCode.NOT_FOUND, '后台账号不存在');
    }
    return data(null);
  }

  return null;
}
